import { useEffect, useState } from 'react';

import { cabinetStorage, loadCabinetPageSize, normalizeCabinetPageSize, saveCabinetPageSize } from './cabinet-model';

/**
 * T.Z119 — the ONE per-user page size both cabinet tables page by (20 rows out of the
 * box). It persists per browser under `cabinet-model`'s `CABINET_PAGE_SIZE_STORAGE_KEY`
 * and is SHARED LIVE: the offline roster's selector and the online table's selector sit
 * on the same screen (T.Z110), and a choice made in one must be the value the other
 * shows and asks for — no reload, no drift.
 *
 * The store is deliberately this small: one module-level value and a listener set.
 * A context would be a second cabinet-wide provider for one number; a URL key cannot
 * work for the offline half, whose state must never meet the online `?groupId=` on
 * the same address (the T.Z62 rule). Storage access is defensive end to end — a
 * `localStorage` that throws degrades to the default, only the persistence is lost.
 */
let stored: number = loadCabinetPageSize(cabinetStorage());
const listeners = new Set<(size: number) => void>();

export function useCabinetPageSize(): [number, (size: number) => void] {
  const [size, setSize] = useState(stored);

  useEffect(() => {
    const notify = (next: number) => setSize(next);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);

  const choose = (raw: number) => {
    const next = normalizeCabinetPageSize(raw);
    if (next === stored) return;
    stored = next;
    saveCabinetPageSize(cabinetStorage(), next);
    for (const listener of listeners) listener(next);
  };

  return [size, choose];
}
